/**
 * Trusscore Estimator - Validation
 *
 * Input validation and BOM comparison with tolerance checking.
 */

import {
  EstimateInput,
  ValidationResult,
  ValidationError,
  BillOfMaterials,
  BOMComparisonResult,
  RoomDimensions,
  WallConfig,
  SlatWallConfig,
  GarageConfig,
  CeilingConfig,
} from '../types';
import {
  MIN_ROOM_DIMENSION,
  MAX_ROOM_DIMENSION,
  MAX_WALL_HEIGHT,
  MIN_WALL_HEIGHT,
} from './constants';

/**
 * Validate room dimensions.
 *
 * @param dimensions - Room dimensions to validate
 * @returns Validation errors (empty if valid)
 */
export function validateDimensions(dimensions: RoomDimensions): ValidationError[] {
  const errors: ValidationError[] = [];

  if (dimensions.length < MIN_ROOM_DIMENSION) {
    errors.push({
      field: 'length',
      message: `Room length must be at least ${MIN_ROOM_DIMENSION} feet`,
    });
  }

  if (dimensions.length > MAX_ROOM_DIMENSION) {
    errors.push({
      field: 'length',
      message: `Room length cannot exceed ${MAX_ROOM_DIMENSION} feet`,
    });
  }

  if (dimensions.width < MIN_ROOM_DIMENSION) {
    errors.push({
      field: 'width',
      message: `Room width must be at least ${MIN_ROOM_DIMENSION} feet`,
    });
  }

  if (dimensions.width > MAX_ROOM_DIMENSION) {
    errors.push({
      field: 'width',
      message: `Room width cannot exceed ${MAX_ROOM_DIMENSION} feet`,
    });
  }

  if (dimensions.height < MIN_ROOM_DIMENSION) {
    errors.push({
      field: 'height',
      message: `Room height must be at least ${MIN_ROOM_DIMENSION} feet`,
    });
  }

  if (dimensions.height > MAX_WALL_HEIGHT) {
    errors.push({
      field: 'height',
      message: `Room height cannot exceed ${MAX_WALL_HEIGHT} feet`,
    });
  }

  return errors;
}

/**
 * Validate SlatWall configuration.
 *
 * @param config - SlatWall configuration
 * @param wallHeight - Wall height for context
 * @returns Validation errors (empty if valid)
 */
export function validateSlatWallConfig(
  config: SlatWallConfig,
  wallHeight: number
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!config.enabled) {
    return errors;
  }

  if (config.startHeight < 0) {
    errors.push({
      field: 'slatWall.startHeight',
      message: 'SlatWall start height cannot be negative',
    });
  }

  if (config.startHeight >= wallHeight) {
    errors.push({
      field: 'slatWall.startHeight',
      message: 'SlatWall start height must be less than wall height',
    });
  }

  if (config.numPanelRows <= 0) {
    errors.push({
      field: 'slatWall.numPanelRows',
      message: 'Number of SlatWall rows must be greater than 0',
    });
  }

  // Maximum rows based on max wall height (6" each; 60ft = 120 rows)
  const maxRows = Math.floor((MAX_WALL_HEIGHT * 12) / 6);
  if (config.numPanelRows > maxRows) {
    errors.push({
      field: 'slatWall.numPanelRows',
      message: `Number of SlatWall rows cannot exceed ${maxRows}`,
    });
  }

  // Check if SlatWall fits within wall
  const slatWallHeight = config.numPanelRows * 0.5; // 6 inches per row
  const slatWallEndHeight = config.startHeight + slatWallHeight;

  if (slatWallEndHeight > wallHeight) {
    errors.push({
      field: 'slatWall',
      message: 'SlatWall extends beyond wall height',
    });
  }

  return errors;
}

/**
 * Validate garage door configuration.
 *
 * @param config - Garage configuration
 * @param wallLength - Wall length for context
 * @param wallHeight - Wall height for context
 * @returns Validation errors (empty if valid)
 */
export function validateGarageConfig(
  config: GarageConfig,
  wallLength: number,
  wallHeight: number
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!config.enabled) {
    return errors;
  }

  if (config.width <= 0) {
    errors.push({
      field: 'garage.width',
      message: 'Garage door width must be greater than 0',
    });
  }

  if (config.height <= 0) {
    errors.push({
      field: 'garage.height',
      message: 'Garage door height must be greater than 0',
    });
  }

  if (config.height > wallHeight) {
    errors.push({
      field: 'garage.height',
      message: 'Garage door height cannot exceed wall height',
    });
  }

  if (config.numDoors <= 0) {
    errors.push({
      field: 'garage.numDoors',
      message: 'Number of garage doors must be greater than 0',
    });
  }

  const totalGarageWidth = config.width * config.numDoors;
  if (totalGarageWidth > wallLength) {
    errors.push({
      field: 'garage',
      message: 'Total garage door width exceeds wall length',
    });
  }

  return errors;
}

/**
 * Validate wall configuration.
 *
 * @param config - Wall configuration
 * @returns Validation errors (empty if valid)
 */
export function validateWallConfig(config: WallConfig): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!config.enabled) {
    return errors;
  }

  if (config.length < MIN_ROOM_DIMENSION) {
    errors.push({
      field: `${config.wallId}.length`,
      message: `Wall ${config.name} length must be at least ${MIN_ROOM_DIMENSION} feet`,
    });
  }

  if (config.length > MAX_ROOM_DIMENSION) {
    errors.push({
      field: `${config.wallId}.length`,
      message: `Wall ${config.name} length cannot exceed ${MAX_ROOM_DIMENSION} feet`,
    });
  }

  if (config.height < MIN_ROOM_DIMENSION) {
    errors.push({
      field: `${config.wallId}.height`,
      message: `Wall ${config.name} height must be at least ${MIN_ROOM_DIMENSION} feet`,
    });
  }

  if (config.height > MAX_WALL_HEIGHT) {
    errors.push({
      field: `${config.wallId}.height`,
      message: `Wall ${config.name} height cannot exceed ${MAX_WALL_HEIGHT} feet`,
    });
  }

  // Validate SlatWall for this wall
  errors.push(...validateSlatWallConfig(config.slatWall, config.height));

  // Validate garage for this wall
  errors.push(...validateGarageConfig(config.garage, config.length, config.height));

  return errors;
}

/**
 * Validate complete estimate input.
 *
 * @param input - Estimate input to validate
 * @returns Validation result with errors if any
 */
export function validateEstimateInput(input: EstimateInput): ValidationResult {
  const errors: ValidationError[] = [];

  // Validate room dimensions
  errors.push(...validateDimensions(input.roomDimensions));

  // Validate ceiling config
  if (input.includeCeiling && input.ceilingConfig) {
    // Basic ceiling validation - just needs to be enabled
    if (input.ceilingZones && input.ceilingZones.length > 0) {
      for (const zone of input.ceilingZones) {
        if (zone.length <= 0 || zone.width <= 0) {
          errors.push({
            field: `ceilingZone.${zone.id}`,
            message: `Ceiling zone ${zone.name} has invalid dimensions`,
          });
        }
      }
    }
  }

  // Validate walls based on calculation mode
  if (input.calculationMode === 'FullRoom' && input.fullRoomWallConfig) {
    // Full room mode - validate garage config against room dimensions
    if (input.fullRoomWallConfig.garage.enabled) {
      const wallForGarage =
        input.fullRoomWallConfig.garage.onWallType === 'LengthWall'
          ? input.roomDimensions.length
          : input.roomDimensions.width;

      errors.push(
        ...validateGarageConfig(
          input.fullRoomWallConfig.garage,
          wallForGarage,
          input.roomDimensions.height
        )
      );
    }

    // Validate SlatWall config
    errors.push(
      ...validateSlatWallConfig(
        input.fullRoomWallConfig.slatWall,
        input.roomDimensions.height
      )
    );
  }

  if (input.calculationMode === 'IndividualWalls' && input.individualWalls) {
    for (const wall of input.individualWalls) {
      errors.push(...validateWallConfig(wall));
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Compare two Bills of Materials with tolerance checking.
 *
 * Used for testing calculation accuracy against expected values.
 *
 * @param expected - Expected BOM
 * @param calculated - Calculated BOM
 * @param tolerancePercent - Acceptable deviation percentage (default 5%)
 * @returns Comparison result with details
 */
export function compareBOMs(
  expected: BillOfMaterials,
  calculated: BillOfMaterials,
  tolerancePercent: number = 5
): BOMComparisonResult {
  const details: BOMComparisonResult['details'] = [];
  let allMatch = true;
  let withinTolerance = true;

  // Helper to compare arrays of items
  const compareItems = (
    category: string,
    expectedItems: { productID: string; quantity: number }[],
    calculatedItems: { productID: string; quantity: number }[]
  ) => {
    const expectedMap = new Map(expectedItems.map(i => [i.productID, i.quantity]));
    const calculatedMap = new Map(calculatedItems.map(i => [i.productID, i.quantity]));

    // Check all expected items
    for (const [productId, expectedQty] of expectedMap) {
      const calculatedQty = calculatedMap.get(productId) || 0;
      const difference = calculatedQty - expectedQty;
      const percentDiff = expectedQty > 0 ? (Math.abs(difference) / expectedQty) * 100 : 0;

      let issue: 'Match' | 'Mismatch' | 'Missing' = 'Match';
      if (calculatedQty === 0) {
        issue = 'Missing';
        allMatch = false;
        withinTolerance = false;
      } else if (difference !== 0) {
        issue = 'Mismatch';
        allMatch = false;
        if (percentDiff > tolerancePercent) {
          withinTolerance = false;
        }
      }

      details.push({
        category,
        productId,
        expectedQty,
        calculatedQty,
        difference,
        percentDiff: Math.round(percentDiff * 100) / 100,
        issue,
      });
    }

    // Check for extra items in calculated
    for (const [productId, calculatedQty] of calculatedMap) {
      if (!expectedMap.has(productId)) {
        details.push({
          category,
          productId,
          expectedQty: 0,
          calculatedQty,
          difference: calculatedQty,
          percentDiff: 100,
          issue: 'Extra',
        });
        allMatch = false;
        // Extra items may be acceptable, so don't fail tolerance check
      }
    }
  };

  // Compare each category
  compareItems('W&CB', expected.wallAndCeilingBoard, calculated.wallAndCeilingBoard);
  compareItems('SlatWall', expected.slatwall, calculated.slatwall);
  compareItems('H-Trim', expected.hTrims, calculated.hTrims);
  compareItems('Snap-In J Trim', expected.jTrimsSnapIn ?? [], calculated.jTrimsSnapIn ?? []);
  compareItems('J-Trim 1/2"', expected.jTrimsHalfInch, calculated.jTrimsHalfInch);
  compareItems('J-Trim 3/4"', expected.jTrimsThreeQuarterInch, calculated.jTrimsThreeQuarterInch);

  return {
    overallMatch: allMatch,
    withinTolerance,
    details,
  };
}

/**
 * Format validation errors for display.
 *
 * @param errors - Array of validation errors
 * @returns Formatted error message
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) {
    return '';
  }

  return errors.map(e => `• ${e.message}`).join('\n');
}

export default {
  validateDimensions,
  validateSlatWallConfig,
  validateGarageConfig,
  validateWallConfig,
  validateEstimateInput,
  compareBOMs,
  formatValidationErrors,
};
